/**
 * PROACTIVE AI SERVICE
 * 
 * AI Coach speaks first
 * Anticipates user state
 * Uses contextual triggers
 * Tone: calm, firm, adult, respectful
 */

import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User, SupportedLanguage } from '../types';
import { getRitualProgress } from './ritualService';
import { getPromiseProgress } from './promiseService';
import { isTodayCoreActionCompleted } from './dailyCoreActionService';
import { calculateLevel, getXPForNextLevel } from './gamificationService';
import i18n from '../i18n/index';

export interface ProactiveMessage {
  text: string;
  priority: 'high' | 'medium' | 'low';
  trigger: string;
}

/**
 * Generate proactive message based on user state
 * Called when AI Coach view loads or user state changes
 */
export const generateProactiveMessage = async (
  user: User,
  lang: SupportedLanguage
): Promise<ProactiveMessage | null> => {
  const t = i18n.t;
  
  // Check if user completed today's core action
  const actionCompleted = isTodayCoreActionCompleted(user);
  
  // Check ritual progress
  const ritual = getRitualProgress(user);
  
  // Check promise progress
  const promise = await getPromiseProgress(user);
  
  // Calculate level progress
  const xpForNext = getXPForNextLevel(user.level);
  const xpNeeded = xpForNext - user.xp;
  const actionsNeeded = Math.ceil(xpNeeded / 50); // Assuming 50 XP per action
  
  // Check yesterday's activity
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  const yesterdayCompleted = user.dailyCoreActionDate === yesterdayStr;
  
  // HIGH PRIORITY: Core action not completed
  if (!actionCompleted) {
    const now = new Date();
    const currentCalendar = (user.monthlyCalendars || []).find(
      cal => cal.year === now.getFullYear() && cal.month === now.getMonth() + 1 && !cal.archived
    );
    const stampsThisMonth = currentCalendar?.totalStamps || 0;
    return {
      text: t('ai_coach.proactive_calendar', { n: stampsThisMonth }),
      priority: 'high',
      trigger: 'core_action_pending'
    };
  }
  
  // HIGH PRIORITY: Ritual in progress
  if (ritual && ritual.isActive) {
    return {
      text: t('ai_coach.proactive_ritual', { n: ritual.currentDay }),
      priority: 'high',
      trigger: 'ritual_active'
    };
  }
  
  // MEDIUM PRIORITY: Close to leveling up
  if (actionsNeeded <= 1 && actionsNeeded > 0) {
    return {
      text: t('ai_coach.proactive_level'),
      priority: 'medium',
      trigger: 'level_proximity'
    };
  }
  
  // MEDIUM PRIORITY: Yesterday missed (no guilt language)
  if (!yesterdayCompleted) {
    return {
      text: t('ai_coach.proactive_missed'),
      priority: 'medium',
      trigger: 'yesterday_missed'
    };
  }
  
  // LOW PRIORITY: Ranking motivation (if we have ranking data)
  // This would require leaderboard service integration
  
  return null;
};

/**
 * Send proactive message to user's coach chat
 * Called automatically when conditions are met
 */
export const sendProactiveMessage = async (
  userId: string,
  message: ProactiveMessage
): Promise<void> => {
  try {
    await addDoc(collection(db, "users", userId, "coachMessages"), {
      role: 'model',
      text: message.text,
      timestamp: serverTimestamp(),
      proactive: true,
      trigger: message.trigger
    });
    console.log(`[PROACTIVE_AI] Sent message to user ${userId}: ${message.trigger}`);
  } catch (error) {
    console.error('[PROACTIVE_AI] Error sending proactive message:', error);
  }
};

/**
 * Check and send proactive messages
 * Should be called periodically or on user state changes
 */
export const checkAndSendProactiveMessages = async (
  user: User,
  lang: SupportedLanguage
): Promise<void> => {
  try {
    // Only send if no messages in last hour (avoid spam)
    // Check recent messages (simplified - no timestamp filter to avoid index issues)
    const recentMessages = query(
      collection(db, "users", user.id, "coachMessages"),
      orderBy("timestamp", "desc"),
      limit(5)
    );
    
    const snap = await getDocs(recentMessages);
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000);
    
    // Check if any recent message is within last hour
    const hasRecentMessage = snap.docs.some(doc => {
      const data = doc.data();
      const timestamp = data.timestamp?.toDate?.()?.getTime() || data.timestamp?.seconds * 1000;
      return timestamp && timestamp > oneHourAgo;
    });
    
    if (hasRecentMessage) {
      // Recent message exists, don't spam
      return;
    }
    
    const message = await generateProactiveMessage(user, lang);
    if (message && message.priority === 'high') {
      await sendProactiveMessage(user.id, message);
    }
  } catch (error) {
    console.error('[PROACTIVE_AI] Error checking proactive messages:', error);
    // Don't throw - proactive messages are non-critical
  }
};

